// Dense typed-array-backed object pools with free-list allocation.
// Conforms to GDD §14.3.

export interface PoolItem {
  id: number;
  alive: boolean;
  pool_index: number;
}

export class Pool<T extends PoolItem> {
  private slots: T[];
  private free: number[];
  private nextId = 1;
  private idSource?: { nextId: number };
  public aliveCount = 0;
  public highWater = 0;

  constructor(
    public readonly capacity: number,
    private readonly factory: (index: number) => T,
    idSource?: { nextId: number }
  ) {
    this.idSource = idSource;
    this.slots = new Array<T>(capacity);
    this.free = new Array<number>(capacity);
    for (let i = capacity - 1; i >= 0; i--) {
      this.slots[i] = factory(i);
      this.slots[i].pool_index = i;
      this.slots[i].alive = false;
      this.free[capacity - 1 - i] = i;
    }
  }

  acquire(): T | null {
    if (this.free.length === 0) return null;
    const index = this.free.pop()!;
    const item = this.slots[index];
    item.id = this.idSource ? this.idSource.nextId++ : this.nextId++;
    item.alive = true;
    item.pool_index = index;
    this.aliveCount++;
    if (this.aliveCount > this.highWater) this.highWater = this.aliveCount;
    return item;
  }

  release(item: T): void {
    if (!item.alive) return;
    item.alive = false;
    this.free.push(item.pool_index);
    this.aliveCount--;
  }

  get(index: number): T {
    return this.slots[index];
  }

  *alive(): Generator<T> {
    for (let i = 0; i < this.capacity; i++) {
      const item = this.slots[i];
      if (item.alive) yield item;
    }
  }

  aliveArray(): T[] {
    const out: T[] = [];
    for (let i = 0; i < this.capacity; i++) {
      const item = this.slots[i];
      if (item.alive) out.push(item);
    }
    return out;
  }

  clear(): void {
    for (let i = 0; i < this.capacity; i++) {
      const item = this.slots[i];
      if (item.alive) {
        item.alive = false;
        this.free.push(i);
      }
    }
    this.aliveCount = 0;
  }
}
